import { useAuth } from "@clerk/react";
import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { PublicFooter, PublicHeader } from "@/components/PublicMarketingLayout";
import {
  createCheckoutSession,
  openBillingPortal,
} from "@/features/billing/billingApi";
import { useAccessSummary } from "@/features/access/useAccessSummary";
import type { AccessSummary, StripeCheckoutProductType } from "@shared/types";

type PricingProductType = StripeCheckoutProductType | "free";

type PricingOption = {
  productType: PricingProductType;
  name: string;
  eyebrow: string;
  price: string;
  access: string;
  bestFor: string;
  bullets: string[];
  trialNote?: string;
};

type ComparisonRow = {
  feature: string;
  free: string;
  standard: string;
  pro: string;
};

type PricingAction = {
  label: string;
  action: "checkout" | "portal" | "sign_up" | "disabled";
  reason?: string;
};

type PlanAction = {
  label: string;
  action: "expand" | "portal" | "sign_up" | "disabled";
};

type VisiblePricingPlan = {
  name: "Free" | "Standard" | "Pro";
  eyebrow: string;
  price: string;
  billingNote: string;
  bestFor: string;
  bullets: string[];
  options: PricingProductType[];
};

const pricingOptions: PricingOption[] = [
  {
    productType: "free",
    name: "Free",
    eyebrow: "A taste of Alchemy",
    price: "$0",
    access: "Free access",
    bestFor: "Best for trying the blueprint workflow with one lightweight idea each month.",
    bullets: [
      "1 blueprint per month",
      "Fast Mode",
      "Assumptions Review",
      "Standard Build Prompt",
      "Basic VIBERC output",
      "No PDF export or saved project library",
    ],
  },
  {
    productType: "standard_one_time",
    name: "Standard",
    eyebrow: "One-time access",
    price: "$27 one-time",
    access: "6 months of Standard access",
    bestFor: "A useful entry-level paid plan for clear, practical app specifications.",
    bullets: [
      "5 blueprints per month",
      "Fast Mode and Guided Mode",
      "Assumptions Review",
      "Standard Build Prompt",
      "Full Build Prompt tab",
      "Basic export and download options",
      "6 months of access",
    ],
  },
  {
    productType: "standard_lifetime",
    name: "Lifetime Standard",
    eyebrow: "Lifetime access",
    price: "$67 one-time",
    access: "Lifetime Standard access",
    bestFor: "The buyer-friendly one-time option for ongoing Standard-quality planning.",
    bullets: [
      "5 blueprints per month, forever",
      "Same quality as Standard",
      "Assumptions Review",
      "Standard Build Prompt",
      "Lifetime access window",
      "No Deep Build mode",
    ],
  },
  {
    productType: "pro_monthly",
    name: "Pro Monthly",
    eyebrow: "Subscription",
    price: "$17/month",
    access: "Full Pro access, monthly",
    bestFor: "Best for serious builders who need deeper planning and monthly flexibility.",
    trialNote: "7-day free trial for new subscribers.",
    bullets: [
      "25 blueprints per month",
      "Deep Build Prompt",
      "Premium Build Quality",
      "Product Spec and detailed Data Model",
      "Acceptance Criteria, Edge Cases and Test Scenarios",
      "PDF export for every blueprint",
      "Launch Readiness Checker",
      "Monetisation Model Suggester",
      "Sales Page Headline Generator",
      "7-day trial for new subscribers",
    ],
  },
  {
    productType: "pro_annual",
    name: "Pro Annual",
    eyebrow: "Subscription",
    price: "$97/year",
    access: "Full Pro access, annual",
    bestFor: "The best annual value for serious builders using Alchemy throughout the year.",
    bullets: [
      "25 blueprints per month",
      "Deep Build Prompt",
      "Premium Build Quality",
      "Full Product Spec, Data Model and QA sections",
      "PDF export for every blueprint",
      "All three Pro commercial tools",
      "Annual billing — best value",
    ],
  },
];

export const visiblePricingPlans: VisiblePricingPlan[] = [
  {
    name: "Free",
    eyebrow: "A taste of Alchemy",
    price: "$0",
    billingNote: "Free access",
    bestFor: "Try the blueprint workflow with one lightweight idea each month.",
    bullets: [
      "1 blueprint per month",
      "Fast Mode and Assumptions Review",
      "Standard Build Prompt",
      "Basic VIBERC output",
      "No PDF export or saved project library",
    ],
    options: ["free"],
  },
  {
    name: "Standard",
    eyebrow: "Clear, practical build planning",
    price: "From $27 one-time",
    billingNote: "6 months access. Lifetime option available at checkout.",
    bestFor: "Entry-level paid access for founders who want useful app specifications without Pro features.",
    bullets: [
      "5 blueprints per month",
      "Fast Mode and Guided Mode",
      "Assumptions Review",
      "Standard Build Prompt and full Build Prompt tab",
      "Up to 5 saved projects",
    ],
    options: ["standard_one_time", "standard_lifetime"],
  },
  {
    name: "Pro",
    eyebrow: "Premium build quality",
    price: "From $17/month",
    billingNote: "Monthly and annual options available.",
    bestFor: "Serious builders who need deeper planning, detailed QA coverage, PDF export, and Pro Tools.",
    bullets: [
      "25 blueprints per month",
      "Deep Build Prompt and Premium Build Quality",
      "Detailed Product Spec and Data Model",
      "Acceptance Criteria, Edge Cases and Test Scenarios",
      "PDF export, unlimited saved projects, and Pro Tools",
    ],
    options: ["pro_monthly", "pro_annual"],
  },
];

export const comparisonRows: ComparisonRow[] = [
  {
    feature: "Monthly blueprints",
    free: "1/month",
    standard: "5/month",
    pro: "25/month",
  },
  {
    feature: "Assumptions Review",
    free: "Included",
    standard: "Included",
    pro: "Included",
  },
  {
    feature: "Standard Build Prompt",
    free: "Included",
    standard: "Included",
    pro: "Included",
  },
  {
    feature: "Deep Build Prompt",
    free: "Not included",
    standard: "Not included",
    pro: "Included",
  },
  {
    feature: "Product Spec",
    free: "Basic output",
    standard: "Included",
    pro: "Included",
  },
  {
    feature: "Data Model",
    free: "Basic",
    standard: "Basic",
    pro: "Detailed",
  },
  {
    feature: "Acceptance Criteria",
    free: "Not included",
    standard: "Not included",
    pro: "Included",
  },
  {
    feature: "Edge Cases",
    free: "Not included",
    standard: "Not included",
    pro: "Included",
  },
  {
    feature: "Test Scenarios",
    free: "Not included",
    standard: "Not included",
    pro: "Included",
  },
  {
    feature: "PDF Export",
    free: "Not included",
    standard: "Not included",
    pro: "Included",
  },
  {
    feature: "Saved Projects",
    free: "No project library",
    standard: "Up to 5",
    pro: "Unlimited",
  },
  {
    feature: "Pro Tools",
    free: "Not included",
    standard: "Not included",
    pro: "Included",
  },
  {
    feature: "Access Window",
    free: "Free access",
    standard: "6 months or lifetime option",
    pro: "Active subscription",
  },
];

function formatEffectiveTier(access: AccessSummary): string {
  if (access.source === "lifetime_standard") {
    return "Lifetime Standard";
  }

  if (access.effectiveTier === "pro") {
    return "Pro";
  }

  if (access.effectiveTier === "standard") {
    return "Standard";
  }

  return "Free";
}

function formatDate(value: string | null): string | null {
  if (!value) {
    return null;
  }

  return new Intl.DateTimeFormat(undefined, {
    month: "short",
    day: "numeric",
    year: "numeric",
  }).format(new Date(value));
}

function getAccessDetail(access: AccessSummary): string {
  const usageDetail = `${access.usage.remainingThisMonth} of ${access.usage.monthlyBlueprintLimit} monthly blueprint generations remain.`;

  if (access.effectiveTier === "free") {
    return usageDetail;
  }

  if (access.source === "lifetime_standard") {
    return `Lifetime Standard access is active and does not expire. ${usageDetail}`;
  }

  if (access.effectiveTier === "standard") {
    const expiresAt = formatDate(access.standard.expiresAt);
    const accessDetail = expiresAt
      ? `Standard access is active until ${expiresAt}.`
      : "Standard access is active.";

    return `${accessDetail} ${usageDetail}`;
  }

  if (access.effectiveTier === "pro") {
    const accessDetail = access.isLifetimeStandard
      ? "Pro access is active. Lifetime Standard is retained for when your subscription ends."
      : "Pro access is active.";

    return `${accessDetail} ${usageDetail}`;
  }

  return "Your current access is active.";
}

function resolvePricingAction(
  productType: PricingProductType,
  access: AccessSummary | null,
): PricingAction {
  if (productType === "free") {
    return {
      label: access
        ? access.effectiveTier === "free"
          ? "Current access"
          : "Included"
        : "Start free",
      action: access ? "disabled" : "sign_up",
      reason: access ? "Free access is available automatically." : undefined,
    };
  }

  if (!access) {
    return {
      label: "Sign up to checkout",
      action: "sign_up",
    };
  }

  const isFree = access.effectiveTier === "free";
  const isLifetime = access.source === "lifetime_standard";
  const isPro = access.effectiveTier === "pro";

  if (productType === "standard_one_time") {
    if (isFree) {
      return { label: "Get Standard", action: "checkout" };
    }

    return {
      label: isPro || isLifetime ? "Included" : "Current plan",
      action: "disabled",
      reason: isPro
        ? "Included in your current plan."
        : "You already have this access.",
    };
  }

  if (productType === "standard_lifetime") {
    if (isLifetime) {
      return {
        label: "Current plan",
        action: "disabled",
        reason: "You already have this access.",
      };
    }

    if (isPro) {
      return access.isLifetimeStandard
        ? {
            label: "Included",
            action: "disabled",
            reason: "Included in your current plan.",
          }
        : { label: "Secure Lifetime Standard", action: "checkout", reason: "Keep Pro now, and retain Lifetime Standard access if your subscription ends." };
    }

    return {
      label: "Secure Lifetime Standard",
      action: "checkout",
    };
  }

  if (productType === "pro_monthly") {
    if (isPro) {
      return {
        label: "Current access",
        action: "disabled",
        reason: "Manage subscription changes in billing.",
      };
    }

    return {
      label: "Start Pro Monthly",
      action: "checkout",
    };
  }

  if (productType === "pro_annual") {
    if (isPro) {
      return {
        label: "Manage billing",
        action: "portal",
        reason: "Manage subscription changes in billing.",
      };
    }

    return {
      label: "Choose Pro Annual",
      action: "checkout",
    };
  }

  return {
    label: "Continue to checkout",
    action: "checkout",
  };
}

function resolvePlanAction(
  plan: VisiblePricingPlan["name"],
  access: AccessSummary | null,
): PlanAction {
  if (plan === "Free") {
    return {
      label: access
        ? access.effectiveTier === "free"
          ? "Current access"
          : "Included"
        : "Start free",
      action: access ? "disabled" : "sign_up",
    };
  }

  if (plan === "Standard") {
    if (access?.effectiveTier === "pro") {
      return { label: "Included in your current plan", action: "disabled" };
    }
    if (access?.effectiveTier === "standard") {
      return { label: "Current access", action: "disabled" };
    }
    return { label: "Choose Standard", action: "expand" };
  }

  if (access?.effectiveTier === "pro") {
    return { label: "Manage billing", action: "portal" };
  }

  return { label: "Choose Pro", action: "expand" };
}

export function Pricing() {
  const { getToken, isLoaded, isSignedIn } = useAuth();
  const navigate = useNavigate();
  const {
    access,
    isLoading: isAccessLoading,
    error: accessError,
  } = useAccessSummary();
  const [activeProduct, setActiveProduct] =
    useState<StripeCheckoutProductType | null>(null);
  const [isPortalLoading, setIsPortalLoading] = useState(false);
  const [portalMessage, setPortalMessage] = useState<string | null>(null);
  const [hasNoBillingAccount, setHasNoBillingAccount] = useState(false);
  const [expandedPlan, setExpandedPlan] = useState<"Standard" | "Pro" | null>(
    null,
  );
  const [errorByProduct, setErrorByProduct] = useState<
    Partial<Record<StripeCheckoutProductType, string>>
  >({});

  async function handleCheckout(productType: StripeCheckoutProductType) {
    if (!isSignedIn) {
      navigate("/sign-up");
      return;
    }

    setActiveProduct(productType);
    setErrorByProduct((current) => ({
      ...current,
      [productType]: undefined,
    }));

    try {
      const token = await getToken();
      const response = await createCheckoutSession(token, productType);
      window.location.href = response.checkoutUrl;
    } catch (error) {
      setErrorByProduct((current) => ({
        ...current,
        [productType]:
          error instanceof Error
            ? error.message
            : "Checkout is unavailable right now.",
      }));
    } finally {
      setActiveProduct(null);
    }
  }

  async function handlePortal() {
    if (!isSignedIn) {
      navigate("/sign-in");
      return;
    }

    if (hasNoBillingAccount) {
      return;
    }

    setIsPortalLoading(true);
    setPortalMessage(null);

    try {
      const token = await getToken();
      const response = await openBillingPortal(token);
      window.location.href = response.portalUrl;
    } catch (error) {
      const message =
        error instanceof Error
          ? error.message
          : "Billing management is unavailable right now.";
      setPortalMessage(message);
      if (message.toLowerCase().includes("no billing account")) {
        setHasNoBillingAccount(true);
      }
    } finally {
      setIsPortalLoading(false);
    }
  }

  return (
    <div className="min-h-screen overflow-x-hidden bg-gradient-to-b from-white via-indigo-50/30 to-white text-slate-950">
      <PublicHeader />
      <main className="mx-auto w-full max-w-6xl space-y-10 px-4 py-10 sm:px-5">
      <section className="overflow-hidden rounded-2xl border border-indigo-100 bg-gradient-to-br from-white via-indigo-50/60 to-violet-50 p-6 shadow-sm sm:p-8">
        <p className="text-sm font-semibold text-indigo-700">Pricing</p>
        <h2 className="mt-2 max-w-3xl text-3xl font-semibold tracking-tight text-slate-950 sm:text-4xl">
          Choose the build quality your next idea deserves
        </h2>
        <p className="mt-3 max-w-3xl text-sm leading-6 text-slate-600">
          Start with a lightweight blueprint, choose Standard for quick and
          clear app specifications, or move to Pro when the build needs deeper
          planning, stronger test coverage, and more implementation detail.
        </p>
        <div className="mt-6 grid gap-3 md:grid-cols-2">
          <div className="rounded-xl border border-slate-200 bg-white/90 p-4">
            <p className="font-semibold text-slate-950">Standard Build Prompt</p>
            <p className="mt-1 text-sm leading-6 text-slate-600">
              Ideal for quick, clear app specs and a practical path into your build.
            </p>
          </div>
          <div className="rounded-xl border border-indigo-200 bg-indigo-950 p-4 text-white shadow-sm">
            <p className="font-semibold">Deep Build Prompt</p>
            <p className="mt-1 text-sm leading-6 text-indigo-100">
              For serious builds where you need fuller planning, stronger test
              coverage, and more implementation detail.
            </p>
          </div>
        </div>
      </section>

      <section className="rounded-xl border border-slate-200 bg-white p-6 shadow-sm">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
          <div>
            <p className="text-sm font-semibold text-slate-500">
              Current access
            </p>
            {!isLoaded || isAccessLoading ? (
              <div className="mt-3 h-7 w-44 animate-pulse rounded bg-slate-100" />
            ) : !isSignedIn ? (
              <>
                <p className="mt-2 text-xl font-semibold text-slate-950">
                  Viewing public pricing
                </p>
                <p className="mt-2 max-w-2xl text-sm leading-6 text-slate-600">
                  Create a free account to start your first blueprint or log in
                  to see your current access.
                </p>
                <div className="mt-4 flex flex-wrap gap-2">
                  <Link
                    to="/sign-up"
                    className="rounded-md bg-indigo-600 px-4 py-2 text-sm font-semibold text-white transition hover:bg-indigo-700"
                  >
                    Start free
                  </Link>
                  <Link
                    to="/sign-in"
                    className="rounded-md border border-slate-300 bg-white px-4 py-2 text-sm font-semibold text-slate-700 transition hover:bg-slate-100"
                  >
                    Log in
                  </Link>
                </div>
              </>
            ) : access ? (
              <>
                <p className="mt-2 text-xl font-semibold text-slate-950">
                  {formatEffectiveTier(access)}
                </p>
                <div className="mt-3 inline-flex rounded-full bg-indigo-50 px-3 py-1 text-xs font-semibold text-indigo-700">
                  {access.generationQuality === "deep"
                    ? "Premium Build Quality · Deep Build Prompt"
                    : "Standard Build Prompt"}
                </div>
                <p className="mt-2 max-w-2xl text-sm leading-6 text-slate-600">
                  {getAccessDetail(access)}
                </p>
              </>
            ) : (
              <p className="mt-2 text-sm leading-6 text-slate-600">
                Access details are unavailable right now.
              </p>
            )}
          </div>
          {isSignedIn ? (
            <button
              type="button"
              onClick={() => void handlePortal()}
              disabled={isPortalLoading || hasNoBillingAccount}
              className="w-fit rounded-md border border-slate-300 bg-white px-4 py-2 text-sm font-semibold text-slate-700 transition hover:bg-slate-100 disabled:cursor-not-allowed disabled:bg-slate-100 disabled:text-slate-400"
            >
              {hasNoBillingAccount
                ? "No billing account"
                : isPortalLoading
                  ? "Opening billing..."
                  : "Manage billing"}
            </button>
          ) : null}
        </div>
        {accessError ? (
          <div className="mt-4 rounded-md border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900">
            {accessError}
          </div>
        ) : null}
        {hasNoBillingAccount ? (
          <div className="mt-4 rounded-md border border-slate-200 bg-slate-50 p-3 text-sm text-slate-600">
            No billing account is on file yet. Choose a plan below to start
            checkout.
          </div>
        ) : portalMessage ? (
          <div className="mt-4 rounded-md border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900">
            {portalMessage}
          </div>
        ) : null}
      </section>

      <section className="grid min-w-0 max-w-full gap-5 lg:grid-cols-3">
        {visiblePricingPlans.map((plan) => {
          const isPro = plan.name === "Pro";
          const planAction = resolvePlanAction(plan.name, access);
          const isExpanded = expandedPlan === plan.name;

          return (
            <article
              key={plan.name}
              className={`min-w-0 flex min-h-full flex-col rounded-2xl border p-6 transition ${
                isPro
                  ? "border-indigo-400 bg-indigo-950 text-white shadow-xl ring-2 ring-indigo-200"
                  : plan.name === "Standard"
                    ? "border-indigo-200 bg-white shadow-md"
                    : "border-slate-200 bg-white shadow-sm"
              }`}
            >
              <p className={`text-sm font-semibold ${isPro ? "text-indigo-200" : "text-indigo-700"}`}>
                {plan.eyebrow}
              </p>
              <h3 className={`mt-2 text-2xl font-semibold ${isPro ? "text-white" : "text-slate-950"}`}>
                {plan.name}
              </h3>
              <p className={`mt-4 text-3xl font-semibold ${isPro ? "text-white" : "text-slate-950"}`}>
                {plan.price}
              </p>
              <p className={`mt-2 text-sm font-medium ${isPro ? "text-indigo-100" : "text-slate-700"}`}>
                {plan.billingNote}
              </p>
              <p className={`mt-3 text-sm leading-6 ${isPro ? "text-indigo-100" : "text-slate-600"}`}>
                {plan.bestFor}
              </p>
              <ul className={`mt-5 flex-1 space-y-2 text-sm ${isPro ? "text-indigo-50" : "text-slate-600"}`}>
                {plan.bullets.map((bullet) => (
                  <li key={bullet} className="flex gap-2">
                    <span
                      aria-hidden="true"
                      className={`shrink-0 font-semibold ${isPro ? "text-emerald-300" : "text-indigo-600"}`}
                    >
                      ✓
                    </span>
                    <span>{bullet}</span>
                  </li>
                ))}
              </ul>

              <button
                type="button"
                onClick={() => {
                  if (planAction.action === "portal") {
                    void handlePortal();
                    return;
                  }
                  if (planAction.action === "sign_up") {
                    navigate("/sign-up");
                    return;
                  }
                  if (planAction.action === "expand") {
                    setExpandedPlan((current) =>
                      current === plan.name
                        ? null
                        : plan.name === "Free"
                          ? null
                          : plan.name,
                    );
                  }
                }}
                disabled={
                  planAction.action === "disabled" ||
                  activeProduct !== null ||
                  isPortalLoading
                }
                aria-expanded={planAction.action === "expand" ? isExpanded : undefined}
                className={`mt-6 w-full rounded-md px-4 py-3 text-sm font-semibold transition disabled:cursor-not-allowed disabled:bg-slate-300 disabled:text-slate-600 ${isPro ? "bg-white text-indigo-950 hover:bg-indigo-50" : "bg-indigo-600 text-white hover:bg-indigo-700"}`}
              >
                {planAction.action === "portal" && isPortalLoading
                  ? "Opening billing..."
                  : planAction.label}
              </button>

              {isExpanded ? (
                <div className={`mt-4 space-y-3 rounded-xl border p-4 ${isPro ? "border-white/15 bg-white/10" : "border-indigo-100 bg-indigo-50/60"}`}>
                  <p className={`text-sm font-semibold ${isPro ? "text-white" : "text-slate-950"}`}>
                    {plan.name === "Standard"
                      ? "Choose your Standard access"
                      : "Choose your Pro billing"}
                  </p>
                  {plan.options.map((productType) => {
                  if (productType === "free") return null;
                  const option = pricingOptions.find(
                    (candidate) => candidate.productType === productType,
                  );
                  if (!option) return null;

                  const isLoading = activeProduct === productType;
                  const error = errorByProduct[productType];
                  const pricingAction = resolvePricingAction(productType, access);
                  const isDisabled =
                    pricingAction.action === "disabled" ||
                    isLoading ||
                    activeProduct !== null ||
                    isPortalLoading;

                  return (
                    <div
                      key={productType}
                      className={`rounded-lg border p-3 ${isPro ? "border-white/15 bg-indigo-900/70" : "border-slate-200 bg-white"}`}
                    >
                      <div className="flex items-start justify-between gap-3">
                        <div>
                          <p className={`text-lg font-semibold ${isPro ? "text-white" : "text-slate-950"}`}>
                            {option.price}
                          </p>
                          <p className={`mt-1 text-xs leading-5 ${isPro ? "text-indigo-100" : "text-slate-600"}`}>
                            {option.access}
                          </p>
                        </div>
                        {option.trialNote ? (
                          <span className="rounded-full bg-emerald-100 px-2 py-1 text-xs font-semibold text-emerald-800">
                            7-day trial
                          </span>
                        ) : null}
                      </div>
                      {error ? (
                        <div className="mt-3 rounded-md border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900">
                          {error}
                        </div>
                      ) : null}
                      <button
                        type="button"
                        onClick={() => {
                          if (pricingAction.action === "portal") {
                            void handlePortal();
                            return;
                          }
                          if (pricingAction.action === "sign_up") {
                            navigate("/sign-up");
                            return;
                          }
                          if (pricingAction.action === "checkout") {
                            void handleCheckout(productType);
                          }
                        }}
                        disabled={isDisabled}
                        className={`mt-3 w-full rounded-md px-4 py-2 text-sm font-semibold transition disabled:cursor-not-allowed disabled:bg-slate-300 ${isPro ? "bg-white text-indigo-950 hover:bg-indigo-50" : "bg-indigo-600 text-white hover:bg-indigo-700"}`}
                      >
                        {isLoading ||
                        (pricingAction.action === "portal" && isPortalLoading)
                          ? "Opening..."
                          : pricingAction.label}
                      </button>
                      {pricingAction.reason ? (
                        <p className={`mt-2 text-xs leading-5 ${isPro ? "text-indigo-200" : "text-slate-500"}`}>
                          {pricingAction.reason}
                        </p>
                      ) : null}
                    </div>
                  );
                })}
                </div>
              ) : null}
            </article>
          );
        })}
      </section>

      <section className="min-w-0 max-w-full overflow-hidden rounded-xl border border-slate-200 bg-white p-4 shadow-sm sm:p-6">
        <div>
          <p className="text-sm font-semibold text-indigo-700">Compare</p>
          <h3 className="mt-2 text-xl font-semibold text-slate-950">
            What's included at each level
          </h3>
          <p className="mt-2 max-w-3xl text-sm leading-6 text-slate-600">
            Compare Standard and Deep Build quality against the access and output sections available today.
          </p>
        </div>

        <div
          className="mt-6 grid gap-3 md:hidden"
          data-testid="pricing-comparison-mobile"
        >
          {comparisonRows.map((row) => (
            <article
              key={row.feature}
              className="min-w-0 rounded-xl border border-slate-200 bg-slate-50 p-4"
            >
              <h4 className="break-words text-sm font-semibold text-slate-950">
                {row.feature}
              </h4>
              <dl className="mt-3 grid gap-2 text-sm">
                <div className="grid min-w-0 grid-cols-[5.5rem_minmax(0,1fr)] gap-3">
                  <dt className="font-semibold text-slate-700">Free</dt>
                  <dd className="min-w-0 break-words text-slate-600">{row.free}</dd>
                </div>
                <div className="grid min-w-0 grid-cols-[5.5rem_minmax(0,1fr)] gap-3">
                  <dt className="font-semibold text-indigo-700">Standard</dt>
                  <dd className="min-w-0 break-words text-slate-600">{row.standard}</dd>
                </div>
                <div className="grid min-w-0 grid-cols-[5.5rem_minmax(0,1fr)] gap-3">
                  <dt className="font-semibold text-indigo-950">Pro</dt>
                  <dd className="min-w-0 break-words text-slate-700">{row.pro}</dd>
                </div>
              </dl>
            </article>
          ))}
        </div>

        <div
          className="mt-6 hidden max-w-full overflow-x-auto overscroll-x-contain pb-2 md:block"
          data-testid="pricing-comparison-scroll"
        >
          <table className="w-full min-w-[620px] border-collapse text-left text-sm">
            <thead>
              <tr className="border-b border-slate-200">
                <th className="py-3 pr-4 font-semibold text-slate-950">
                  Feature
                </th>
                <th className="px-4 py-3 font-semibold text-slate-950">Free</th>
                <th className="px-4 py-3 font-semibold text-slate-950">
                  Standard
                </th>
                <th className="px-4 py-3 font-semibold text-slate-950">Pro</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {comparisonRows.map((row) => (
                <tr key={row.feature} className="transition hover:bg-slate-50/80">
                  <td className="py-3 pr-4 font-medium text-slate-950">
                    {row.feature}
                  </td>
                  <td className="px-4 py-3 text-slate-600">{row.free}</td>
                  <td className="px-4 py-3 text-slate-600">{row.standard}</td>
                  <td className="px-4 py-3 text-slate-600">{row.pro}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>
      </main>
      <PublicFooter />
    </div>
  );
}
